import {
  Link,
  Outlet,
  createRootRouteWithContext,
  redirect,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  CalendarDays,
  Home as HomeIcon,
  LogOut,
  Menu,
  Search,
  User,
  X,
} from "lucide-react";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { BRAND, GOAL_LABELS } from "@omina/core";
import Logo from "../components/Logo";
import { signOut } from "../lib/auth";
import { ensureMe, useSettings } from "../lib/queries";

export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * Where a founder who has not finished onboarding is allowed to be.
 *
 * The flow itself, plus `/settings`. Settings is on the list on purpose: it
 * holds sign-out and the Google card, and a gate with no way out is a gate
 * that turns a bad answer into a locked account. `/login` never reaches the
 * check — the guard returns before it.
 */
const ONBOARDING = new Set([
  "/interests",
  "/curation",
  "/path",
  "/matches",
  "/settings",
]);

export const Route = createRootRouteWithContext<RouterContext>()({
  /**
   * The auth guard.
   *
   * `/api/me` is the single source of truth — one request that answers "who am
   * I, which org, and is intake done". A 401 resolves to null (see meQuery),
   * so signed-out is a value to branch on rather than an exception to catch.
   *
   * It runs through the query client, which means a client-side navigation
   * between guarded routes reuses the cached answer instead of re-asking on
   * every click.
   */
  beforeLoad: async ({ context, location }) => {
    // /login is the one page a signed-out founder is allowed to see. Guarding
    // it too would redirect it to itself, forever.
    if (location.pathname === "/login") return;

    const me = await ensureMe(context.queryClient);
    if (!me) {
      throw redirect({
        to: "/login",
        // Where she was going, so sign-in can put her back there.
        search: { redirect: location.href },
        replace: true,
      });
    }

    /**
     * The onboarding gate.
     *
     * It lives here rather than in the sign-in button, because the button
     * could not make it stick. Sign-in computed a destination — interests for
     * a new founder — and then handed it to `redirect`, which the guard above
     * sets on *every* bounce: open the app at `/`, get sent to `/home`, get
     * bounced to `/login?redirect=/home`, and sign-in dutifully returns her to
     * `/home`. The onboarding destination was unreachable code, so approving
     * Google landed on the dashboard and the interest questions never ran.
     *
     * Deciding here fixes the whole class rather than that one path: a deep
     * link, a refresh halfway through, a founder who closes the tab on
     * question three and comes back tomorrow all arrive at the same answer.
     *
     * `interestsDone` is the signal, and it is deliberately the *only* one —
     * see the note on `/api/me`. It marks the step everything downstream
     * depends on; a founder who got past it and then abandoned the goal screen
     * is let through to the product rather than pinned to a flow she already
     * walked.
     */
    if (!me.interestsDone && !ONBOARDING.has(location.pathname)) {
      throw redirect({ to: "/interests", replace: true });
    }
  },
  component: Shell,
  notFoundComponent: NotFound,
});

/**
 * The nav:
 *
 *     today  →  where I'm going  →  who I met  →  settings
 *
 * Intake and Interview used to open this list, on the reasoning that intake is
 * where every question is *authored* — the industry, the buyer, the city — so
 * the one editable surface should not be buried under the screens it feeds.
 *
 * They are gone from here and the reasoning survives them: intake is linked
 * from the six screens that actually need it (the radar when the industry is
 * missing, follow-up and prep when there is nothing to work with, the
 * workspace card), so it is offered at the moment it is the answer rather than
 * standing in a menu asking to be remembered. A drawer is for moving between
 * places, not for holding a setup step she does once.
 *
 * Circle and Settings close the list ungrouped: a heading over a single link
 * labels nothing.
 *
 * Follow-up and Prep were the other two thirds of "who I met" and are out of
 * the drawer for now — the routes are untouched and still reachable, they are
 * just not offered here. Taking them out took the heading with them, since one
 * link is not a beat. When they come back, so does the group.
 *
 * The track this all points at is shown below, in `TrackStatus` — displayed
 * there, set on the radar, and nowhere else.
 */
const BEATS = [
  {
    label: "where I'm going",
    links: [
      // Rooms first: it is where she decides, and the calendar only has
      // anything in it once she has picked something there. Named for what it
      // holds rather than for the screen it used to be — "Radar" was a second
      // name for the feed from the moment the two merged.
      { to: "/feed", label: "Rooms" },
      { to: "/calendar", label: "Calendar" },
      // Last, because it is the only one that presupposes the other two: a
      // brief exists for a room already on her calendar. Decide, commit, then
      // read the room — that is the order she does it in.
      { to: "/brief", label: "Room brief" },
    ],
  },
] as const;

function Shell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);

  /** Close on navigation. Without this the drawer stays open over the screen
   *  she just chose, and she has to dismiss the thing that did its job. */
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Sign-in gets the brand and nothing else: a nav full of links she cannot
  // open yet is noise on the one page whose job is a single click.
  const bare = pathname === "/login";

  /**
   * Onboarding gets the same treatment, for a different reason.
   *
   * She *can* use the nav on curation, interests and path — she is signed in —
   * which is exactly why it is removed. Each is one task with an end: six
   * rooms to judge, four questions to answer, one goal to pick. The only thing
   * five other links do is offer her somewhere else to be halfway through, and
   * these are the screens whose value comes from finishing them.
   *
   * Neither screen carries the positioning line at the foot. "Make your
   * technology legible" is a claim about what Omina does, which is worth
   * reading somewhere she is not mid-task.
   */
  const focused =
    bare ||
    pathname === "/curation" ||
    pathname === "/interests" ||
    pathname === "/path" ||
    pathname === "/matches" ||
    // The four blueprint conversations, one per track.
    pathname === "/icp" ||
    pathname === "/authority" ||
    pathname === "/funding" ||
    pathname === "/learning";

  if (focused) {
    return (
      <>
        <div className="wrap">
          {/* No aura on curation.

              It is a radial amber-to-coral wash behind the top of the page,
              and on paper it reads as daylight. On the midnight ground it is a
              warm glow sitting directly behind the card — a gradient across
              the one screen that is meant to be flat, and the thing that still
              looked gradient-y in dark mode after the card itself was
              flattened. Sign-in keeps it: nothing there has to stay still. */}
          {bare && <div className="aurora" aria-hidden />}
          {/* Sign-in shows the mark alone and closes with the name at the
              foot; curation has no foot, so it takes mark and name together
              at the top. */}
          <header className="tk-header authhead">
            <Logo markOnly={bare} />
          </header>
          <Outlet />
          {bare && <footer className="tk-footer authmark">{BRAND.wordmark}</footer>}
        </div>
      </>
    );
  }

  return (
    <>
      {/*
        The app chrome: a bar at the top, a drawer behind it, tabs at the foot.

        The sidebar used to be permanent, which on a phone meant the loop's
        table of contents ate a third of every screen. It is the same nav — the
        beats, the order, the track — but behind a button, because a founder
        reading her feed does not need the map of the product visible at all
        times.

        The tab bar carries the four places she actually moves between. Icons
        only: at this size a label under each turns a bar into a paragraph, and
        four destinations are few enough to learn. Every one keeps an
        `aria-label`, so "icons only" is a visual decision rather than an
        accessibility one.
      */}
      <div className="shell">
        <div className="aurora" aria-hidden />

        <header className="topbar">
          <button
            type="button"
            className="topmenu"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Menu size={20} aria-hidden />
          </button>
          <span className="topname">{BRAND.wordmark}</span>
          {/* A spacer the same width as the button, so the name is centred on
              the screen rather than on the space left over beside it. */}
          <span className="topspacer" aria-hidden />
        </header>

        {/* The drawer. Rendered always and moved off-canvas, so opening it is
            a transform rather than a mount — a nav that fades in from nothing
            reads as slower than it is. */}
        <div
          className={menuOpen ? "scrim isopen" : "scrim"}
          onClick={() => setMenuOpen(false)}
          aria-hidden
        />
        <aside className={menuOpen ? "sidebar isopen" : "sidebar"}>
          <header className="tk-header">
            <Logo />
            <button
              type="button"
              className="drawerclose"
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
            >
              <X size={18} aria-hidden />
            </button>
          </header>

          <nav aria-label="The loop" onClick={() => setMenuOpen(false)}>
            <Link
              to="/home"
              className="side-link"
              activeProps={{ className: "side-link active" }}
              activeOptions={{ exact: true }}
            >
              Today
            </Link>

            {BEATS.map((beat) => (
              <div className="side-group" key={beat.label}>
                <div className="side-heading">{beat.label}</div>
                {beat.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="side-link"
                    activeProps={{ className: "side-link active" }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            ))}

            {/* Ungrouped: see the note on BEATS. */}
            <Link
              to="/circle"
              className="side-link"
              activeProps={{ className: "side-link active" }}
            >
              Circle
            </Link>

            {/* Ungrouped, at the foot: it is not a beat of the loop, it is
                where the loop is configured. */}
            <Link
              to="/settings"
              className="side-link"
              activeProps={{ className: "side-link active" }}
            >
              Settings
            </Link>
          </nav>

          {/* The foot: what the loop is pointed at, and the way out.

              Sign-out lives here as well as in Settings on purpose — it is not
              a preference, it is the end of a session, and asking a founder to
              open a menu of settings to leave is a step too many at a shared
              laptop. Both call the same `leave()`; neither is the "real" one. */}
          <div className="sidefoot">
            <TrackStatus />
            <SignOutLink />
          </div>
        </aside>

        <main className="content">
          <Outlet />
        </main>

        <nav className="tabbar" aria-label="Main">
          <Tab to="/home" label="Home" icon={<HomeIcon size={21} aria-hidden />} exact />
          <Tab to="/feed" label="Search" icon={<Search size={21} aria-hidden />} />
          <Tab to="/calendar" label="Calendar" icon={<CalendarDays size={21} aria-hidden />} />
          <Tab to="/profile" label="Profile" icon={<User size={21} aria-hidden />} />
        </nav>
      </div>
    </>
  );
}

/**
 * One tab. The label is the accessible name and never renders — see the note
 * on the tab bar above.
 */
function Tab({
  to,
  label,
  icon,
  exact,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
  exact?: boolean;
}) {
  return (
    <Link
      to={to}
      aria-label={label}
      className="tab"
      activeProps={{ className: "tab active" }}
      activeOptions={exact ? { exact: true } : undefined}
    >
      {icon}
    </Link>
  );
}

/**
 * Sign out, from the drawer.
 *
 * A button rather than a `Link`: there is nowhere to go until the session is
 * actually gone, and a link would let a founder land on `/login` with a live
 * cookie behind her.
 *
 * Clearing the cache is not optional. The SPA caches every answer, and
 * invalidating only marks it stale — so the next person at this browser sees
 * the last one's loop for the frame before the refetch lands. `clear()`, then
 * navigate (AGENTS.md).
 *
 * `leaving` guards the double tap: the drawer stays on screen for the length
 * of the round trip, and two taps would fire two sign-outs and two navigations.
 */
function SignOutLink() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [leaving, setLeaving] = useState(false);

  async function leave() {
    if (leaving) return;
    setLeaving(true);
    try {
      await signOut();
      queryClient.clear();
      await router.navigate({ to: "/login", replace: true });
    } finally {
      setLeaving(false);
    }
  }

  return (
    <button type="button" className="side-out" disabled={leaving} onClick={() => void leave()}>
      <LogOut size={15} aria-hidden />
      {leaving ? "Signing out\u2026" : "Sign out"}
    </button>
  );
}

/**
 * The current track — shown, not set.
 *
 * This used to be the fourth control writing the same field, alongside intake,
 * the radar and settings. Four controls on one value do not read as a shared
 * setting; they read as the app losing her choice, because changing it in one
 * place silently moved the other three.
 *
 * So the radar keeps the only control — it is the screen where switching has a
 * visible consequence, and watching the rooms re-rank *is* the feedback that
 * makes the control legible. Here it becomes a status line that says what the
 * whole product is currently pointed at, and a way back to where that is
 * decided.
 */
function TrackStatus() {
  const settings = useSettings();
  const active = settings.data?.activeGoal;

  if (!settings.isSuccess || !active) return null;

  return (
    <div className="track-picker">
      <div className="side-heading">working on</div>
      {/* Settings, because that is where the track is set. It pointed at the
          radar back when the radar owned the switcher; the switcher moved and
          the radar is gone, so this was sending her to a screen that could not
          do the thing the line implies. */}
      <Link to="/settings" className="track-option" aria-current={undefined}>
        {GOAL_LABELS[active].title}
      </Link>
    </div>
  );
}


function NotFound() {
  return (
    <div className="card">
      <h1 className="tk">Nothing here</h1>
      <p className="lede">That address isn&rsquo;t part of the loop.</p>
      <Link to="/home" className="btn" style={{ textDecoration: "none" }}>
        Back to today
      </Link>
    </div>
  );
}
